/**
 * InlineServeRow (PROD-1428, Abner's row, round-3 design pass) — one student
 * row of the serve line, with everything that used to be a separate student
 * page happening IN the row:
 *
 *   [ name           | 1 French Toast Sticks, Whole Grain Rich | Serve   ]
 *   [ ID · Grade     | 2 Links Pork Sausage Links, Lower Sodium|         ]
 *   [ tags           |                                         |         ]
 *
 * Measured from the Figma mock (desktop card 1068 wide): 16px card radius,
 * 1px border, no shadow; 16px row padding top/bottom, 20px left/right, 16px
 * column gap, columns top-aligned; the NAME column is 240px including its
 * 20px right padding and its divider spans the row; the ACTION column is
 * 214px (tablet: 200 / 170). Name 24/32 SemiBold, meta 16/24 muted 4px
 * under it, tag chips 12px under the meta. Item rows 18/28 with a SemiBold
 * prefix, plain text lines 8px apart with NO row chrome — while editing
 * they carry a 32px check square (the `OrderedChecklist`) and the Milk radio
 * follows 24px below. Action column: a 72px pill in the main spot (soft
 * `Serve` / primary `Confirm` / ghost `Undo`), then while editing a 1px
 * separator, `+ Sell items` (outline, 56px), `Cancel` (muted fill, 56px),
 * `Report issue` (ghost, 56px), 16px apart. `+ Sell items` opens this
 * card in a MODAL (round 9): its left column is this row's meal column
 * (`ServeRowMeal`, the same checklist / Milk / OVS warning on the same
 * state), the register on the right, one commit pill across the bottom.
 *
 * Baselines: the first item line (18/28) shares the name's (24/32) baseline
 * across the divider — top-aligned line boxes would float it 5px higher, so
 * the meal column carries that 5px at the three-column tier
 * (`SERVE_ROW_MEAL_COLUMN_CLASS`); the stacked tiers have no shared line.
 *
 * Hierarchy: the name and the primary pill are the only strong elements;
 * items, meta, chips and secondary pills recede (muted, regular weights).
 * Nothing in the row is bolder than 600, nothing is tracked tight, and
 * size and position — never weight — make a thing stand out (the pills
 * override the Button base's tight tracking, too). The served /
 * failed note sits under the meta in the NAME column. Every button is a
 * pill (the app's `--btn-radius` rule, spelled out as `rounded-full`).
 *
 * NARROW CARDS (round 7, an iPad mini in landscape tablet mode: the rows
 * column is ~500px beside the keypad, and 200 / fill / 170 left the meal
 * column ~100px wide, one word per line): the row reads its OWN width with a
 * CSS container query (`@container` on the card, Tailwind 4 `@[Npx]:`
 * variants on the grid), never the viewport, because the split layout
 * (the keypad column) decides the width. Below the threshold
 * the row STACKS: row 1 = name + `ID · Grade` + chips laid out horizontally,
 * a separator (the same border token as the column divider), row 2 = the
 * item lines with the action column on the right. Below 560px the action
 * column drops under the items too. The threshold is the smallest card at
 * which the three columns still give the meal column a sane 280px line —
 * derived from the column constants below.
 *
 * The row is a tap target too: idle → open (a no-order row → its Sell items
 * modal), served → review (its items). An idle row the register may charge
 * carries `+ Sell items` under its main pill, so no one has to open a row to
 * find it (PROD-1561).
 * Purely presentational — `ServeStudentRow` owns the state and the writes.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { Plus } from 'lucide-react'
import type { MouseEvent, ReactNode } from 'react'

import type { StudentAllergenWire } from '@/api/cafeteria'
import type { DeliverableLineBucket } from '@/lib/cafeteria/format'
import type { ServeAction, ServeRowStatus } from '@/lib/cafeteria/serve-row'
import type { ServeRowMode } from '@/lib/cafeteria/serve-view'

import { StudentTags } from './student-tags'

export type InlineRowMode = ServeRowMode | 'idle'

export interface InlineRowAction extends ServeAction {
  disabled?: boolean
}

export interface InlineServeRowProps {
  name: string
  studentId: string | null
  gradeLabel: string | null
  tags: ReadonlyArray<DeliverableLineBucket>
  allergens?: ReadonlyArray<StudentAllergenWire>
  /** The meal column's item rows (`OrderedChecklist` in any guise); null for a roster kid with no order. */
  items: ReactNode | null
  /** The Milk radio under the items while editing; null otherwise. */
  milk?: ReactNode | null
  /** Anything else the meal column shows while editing (the OVS warning). */
  extra?: ReactNode | null
  status: ServeRowStatus
  mode: InlineRowMode
  /** "Served at 11:42" / "Served 2 of 3 at … · Not served: …" (green) or the failure line (red). */
  note: string | null
  /** The one fixed-position pill; null renders nothing there. */
  action: InlineRowAction | null
  /**
   * "+ Sell items" (editing, where the register exists, or an idle row the
   * register may charge); `active` while its modal is open.
   */
  sell?: { active: boolean; onClick: () => void } | null
  /** An idle no-order row: a tap opens its Sell items modal. */
  sellOnTap?: boolean
  readOnly: boolean
  /** Tablet mode: the narrower name and action columns. */
  compact?: boolean
  onRowTap: () => void
  onAction: () => void
  onCancel: () => void
  onReportIssue?: () => void
}

// ── Column facts (px) and the container-query thresholds derived from them ──
// name column (incl. its 20px right padding) · column gap · meal line · gap · action column · card padding.
export const ROW_NAME_COLUMN_PX = { compact: 200, desktop: 240 } as const
export const ROW_ACTION_COLUMN_PX = { compact: 170, desktop: 214 } as const
export const ROW_COLUMN_GAP_PX = 16
export const ROW_CARD_PADDING_X_PX = 20
/** The narrowest meal line worth keeping beside the name and the pill. */
export const ROW_MIN_MEAL_LINE_PX = 280
/**
 * Card width at which the three columns switch on:
 *   compact: 200 + 16 + 280 + 16 + 170 + 2×20 = 722
 *   desktop: 240 + 16 + 280 + 16 + 214 + 2×20 = 806
 * The literal `@[722px]` / `@[806px]` variants below MUST equal these.
 */
export const ROW_THREE_COLUMNS_MIN_PX = {
  compact:
    ROW_NAME_COLUMN_PX.compact +
    ROW_COLUMN_GAP_PX +
    ROW_MIN_MEAL_LINE_PX +
    ROW_COLUMN_GAP_PX +
    ROW_ACTION_COLUMN_PX.compact +
    2 * ROW_CARD_PADDING_X_PX,
  desktop:
    ROW_NAME_COLUMN_PX.desktop +
    ROW_COLUMN_GAP_PX +
    ROW_MIN_MEAL_LINE_PX +
    ROW_COLUMN_GAP_PX +
    ROW_ACTION_COLUMN_PX.desktop +
    2 * ROW_CARD_PADDING_X_PX,
} as const
/** Below this card width even the stacked row's action column goes under the items. */
export const ROW_TWO_COLUMNS_MIN_PX = 560

/**
 * The grid per density. Tailwind scans for literal class strings, so each
 * tier is spelled out: one column, then items + action column (stacked),
 * then the three columns once the card is wide enough.
 */
export const SERVE_ROW_GRID_CLASS = {
  compact: 'grid-cols-1 @[560px]:grid-cols-[minmax(0,1fr)_170px] @[722px]:grid-cols-[200px_minmax(0,1fr)_170px]',
  desktop: 'grid-cols-1 @[560px]:grid-cols-[minmax(0,1fr)_214px] @[806px]:grid-cols-[240px_minmax(0,1fr)_214px]',
} as const

/**
 * The name column per density: stacked, it is row 1 across the card (name,
 * meta and chips in a line, a separator under it); three-column, it is the
 * left column behind the divider.
 */
export const SERVE_ROW_NAME_COLUMN_CLASS = {
  compact:
    'flex min-w-0 flex-row flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-3 @[560px]:col-span-2 @[722px]:col-span-1 @[722px]:flex-col @[722px]:items-stretch @[722px]:gap-0 @[722px]:self-stretch @[722px]:border-b-0 @[722px]:border-r @[722px]:pb-0 @[722px]:pr-5',
  desktop:
    'flex min-w-0 flex-row flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-3 @[560px]:col-span-2 @[806px]:col-span-1 @[806px]:flex-col @[806px]:items-stretch @[806px]:gap-0 @[806px]:self-stretch @[806px]:border-b-0 @[806px]:border-r @[806px]:pb-0 @[806px]:pr-5',
} as const

/**
 * The meal column at the three-column tier: Outfit 24/32 puts the name's
 * baseline 5px lower than an 18/28 line's in a top-aligned box, so the
 * first item line is nudged down to share it. The skeleton mirrors it.
 */
export const SERVE_ROW_MEAL_COLUMN_CLASS = {
  compact: '@[722px]:pt-[5px]',
  desktop: '@[806px]:pt-[5px]',
} as const

/** Inside the name column: the meta line, chips and note sit inline when stacked, under the name in a column. */
const NAME_META_CLASS = {
  compact: 'text-base leading-6 text-muted-foreground tabular-nums @[722px]:mt-1',
  desktop: 'text-base leading-6 text-muted-foreground tabular-nums @[806px]:mt-1',
} as const
const NAME_TAGS_CLASS = {
  compact: 'flex flex-wrap gap-1.5 @[722px]:mt-3',
  desktop: 'flex flex-wrap gap-1.5 @[806px]:mt-3',
} as const
const NAME_NOTE_CLASS = {
  compact: 'w-full text-base font-medium leading-6 @[722px]:mt-3',
  desktop: 'w-full text-base font-medium leading-6 @[806px]:mt-3',
} as const

/** The main spot: 72px tall, 16 Medium. */
const MAIN_PILL = 'h-[72px] w-full rounded-full text-base font-medium tracking-normal shadow-none'
/** The secondary pills under the separator: 56px tall, 16 Medium. */
const PILL = 'h-14 w-full rounded-full text-base font-medium tracking-normal shadow-none'
const SOFT = 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary'

function stop(event: MouseEvent<HTMLElement>): void {
  event.stopPropagation()
}

/** The meta line under the name: `ID · Grade`, either part left out when missing. */
export function serveRowMeta(studentId: string | null, gradeLabel: string | null): string {
  return [studentId, gradeLabel].filter((part): part is string => Boolean(part)).join(' · ')
}

/**
 * The meal column's stack: the item rows (the checklist while editing), then
 * the Milk radio and the OVS warning, 24px apart; "No order today" for a
 * roster kid with no order. The row's middle column AND the Sell items
 * modal's left column render this, so the two read the same (round 9).
 */
export function ServeRowMeal({
  items,
  milk = null,
  extra = null,
  testId = 'serve-row-meal',
  className,
}: {
  items: ReactNode | null
  milk?: ReactNode | null
  extra?: ReactNode | null
  testId?: string
  /** Layout only (the row's baseline nudge); the Sell items modal passes nothing. */
  className?: string
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-6', className)} data-testid={testId}>
      {items ?? <p className="text-lg leading-7 text-muted-foreground">No order today</p>}
      {milk}
      {extra}
    </div>
  )
}

export function InlineServeRow({
  name,
  studentId,
  gradeLabel,
  tags,
  allergens = [],
  items,
  milk = null,
  extra = null,
  status,
  mode,
  note,
  action,
  sell = null,
  sellOnTap = false,
  readOnly,
  compact = false,
  onRowTap,
  onAction,
  onCancel,
  onReportIssue,
}: InlineServeRowProps) {
  const done = status === 'served' || status === 'partial' || status === 'pending'
  const editing = mode === 'edit'
  const meta = serveRowMeta(studentId, gradeLabel)
  // Idle rows open on a tap (a no-order row into its Sell items modal);
  // served rows review on a tap; an open row's surface holds the
  // checkboxes, so it is not a tap target of its own.
  const idleTap = mode === 'idle' && (status === 'ready' || sellOnTap)
  const tappable = !readOnly && (idleTap || done)
  const soft = mode === 'idle' && status === 'ready'
  const density = compact ? 'compact' : 'desktop'
  const sellButton = sell ? (
    <Button
      type="button"
      variant="outline"
      aria-pressed={sell.active}
      className={cn(PILL, 'text-primary hover:text-primary', sell.active && 'border-primary bg-primary/5')}
      data-testid="serve-row-sell"
      onClick={sell.onClick}>
      <Plus className="size-4" aria-hidden />
      Sell items
    </Button>
  ) : null

  return (
    <article
      data-testid="serve-row"
      data-status={status}
      data-mode={mode}
      data-density={density}
      className={cn(
        // `@container`: the grid below queries THIS card's width, not the viewport.
        '@container rounded-2xl border bg-background transition-colors',
        done && 'border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/15',
        status === 'failed' && 'border-destructive/40',
        // The open card stands out from the list, including behind the Sell items modal.
        mode === 'edit' && 'border-primary ring-1 ring-inset ring-primary',
      )}>
      <div
        role={tappable ? 'button' : undefined}
        tabIndex={tappable ? 0 : undefined}
        onClick={tappable ? onRowTap : undefined}
        onKeyDown={
          tappable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onRowTap()
                }
              }
            : undefined
        }
        className={cn(
          // `rounded-[inherit]`: the hover fill and the focus ring follow the card's corners.
          'grid items-start gap-x-4 gap-y-3 rounded-[inherit] px-5 py-4 transition-colors',
          SERVE_ROW_GRID_CLASS[density],
          tappable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          // A pointer gets a quiet lift on the rows a tap acts on; touch never sees it.
          tappable && idleTap && 'hover:bg-muted/60',
          tappable && done && 'hover:bg-emerald-500/5',
        )}
        data-testid="serve-row-grid">
        {/* Name column: name, ID · Grade, tags, the served note — behind a
            divider that spans the row, or (a narrow card) the first row across
            the card over a separator. */}
        <div className={SERVE_ROW_NAME_COLUMN_CLASS[density]} data-testid="serve-row-name-column">
          <span className="text-[24px] font-semibold leading-8 text-foreground" data-testid="serve-row-name">
            {name}
          </span>
          {meta ? <span className={NAME_META_CLASS[density]}>{meta}</span> : null}
          {allergens.length > 0 || tags.length > 0 ? (
            <div className={NAME_TAGS_CLASS[density]} data-testid="serve-row-tags">
              <StudentTags allergens={allergens} tags={tags} />
            </div>
          ) : null}
          {note ? (
            <p
              data-testid="serve-row-note"
              className={cn(
                NAME_NOTE_CLASS[density],
                status === 'failed' ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-400',
              )}>
              {note}
            </p>
          ) : null}
        </div>

        {/* Meal column: the item rows (the checklist while editing), then the Milk radio 24px below. */}
        <ServeRowMeal
          items={items}
          milk={editing ? milk : null}
          extra={editing ? extra : null}
          className={SERVE_ROW_MEAL_COLUMN_CLASS[density]}
        />

        {/* Action column: fixed width, the main pill always in the same spot. */}
        <div className="flex flex-col gap-4 self-start" data-testid="serve-row-actions" onClick={stop}>
          {readOnly ? null : (
            <>
              {action ? (
                <Button
                  type="button"
                  variant={soft || done ? 'ghost' : 'default'}
                  className={cn(MAIN_PILL, soft && SOFT, done && 'text-muted-foreground hover:text-foreground')}
                  disabled={action.disabled}
                  data-testid="serve-row-action"
                  onClick={onAction}>
                  {action.label}
                </Button>
              ) : null}
              {/* Idle: `+ Sell items` right under the main pill (PROD-1561). */}
              {!editing ? sellButton : null}
              {editing ? (
                <>
                  <div className="border-t" aria-hidden />
                  {sellButton}
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(PILL, 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground')}
                    data-testid="serve-row-cancel"
                    onClick={onCancel}>
                    Cancel
                  </Button>
                  {onReportIssue ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className={cn(PILL, 'text-muted-foreground')}
                      data-testid="serve-row-report-issue"
                      onClick={onReportIssue}>
                      Report issue
                    </Button>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </article>
  )
}
